/**
 * Supabaseマスターデータサービス
 * 取引先、車両モデル、メーカー、車種などのマスターデータ管理を担当
 */

import { SupabaseClient } from '@supabase/supabase-js';
import logger from '../../utils/logger';
import { getSupabaseClient } from '../../utils/supabase';
import {
  BusinessPartner,
  BusinessPartnerDataSource,
  BusinessPartnerCategory,
  BusinessPartnerType,
  VehicleModel,
  VehicleModelDataSource,
  VehicleType,
  Manufacturer,
} from '../../types';
import { DataServiceError, ErrorCodes } from '../dataService';

export class SupabaseMasterService {
  public supabase: SupabaseClient | null = null;

  constructor() {
    this.initializeSupabase();
  }

  private initializeSupabase() {
    try {
      this.supabase = getSupabaseClient();
      if (!this.supabase) {
        logger.warn('Supabaseの設定が見つかりません。環境変数を確認してください。');
        return;
      }
      logger.info('✅ SupabaseMasterService: シングルトンクライアント取得完了');
    } catch (error) {
      logger.error('❌ SupabaseMasterService initialization failed:', error);
    }
  }

  private ensureSupabaseClient() {
    if (!this.supabase) {
      throw new DataServiceError(
        'Supabaseクライアントが初期化されていません',
        ErrorCodes.CLIENT_INIT_FAILED
      );
    }
  }

  // =============================================================================
  // BusinessPartner関連メソッド
  // =============================================================================

  /**
   * システム設定画面用：全ての取引先を取得（会社フィルタなし）
   * 陸送会社管理者がシステム設定で全取引先から選択できるようにする
   * @returns 全取引先データ
   */
  async getAllBusinessPartners(): Promise<BusinessPartner[]> {
    logger.info(
      '🎯 SupabaseMasterService: getAllBusinessPartners開始 - 全取引先取得（フィルタなし）'
    );
    this.ensureSupabaseClient();

    try {
      const { data: businessPartnersData, error } = await this.supabase!.from('business_partners')
        .select('*')
        .order('name');

      if (error) {
        throw new DataServiceError(
          `取引先データの取得に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR,
          error
        );
      }

      const businessPartners = (businessPartnersData || []).map((partner: any) => ({
        id: partner.id,
        name: partner.name,
        displayName: partner.display_name || partner.name,
        type: partner.type as BusinessPartnerType,
        category: partner.category as BusinessPartnerCategory,
        availableRoles: partner.available_roles || [],
        defaultAddress: partner.default_address,
        defaultPhone: partner.default_phone,
        defaultEmail: partner.default_email || '',
        defaultContactPerson: partner.default_contact_person || '',
        notes: partner.notes || '',
        status: partner.status,
        companyId: partner.company_id,
        dataSource: partner.data_source as BusinessPartnerDataSource,
        locations: partner.locations || [],
        isSystemPreset: partner.is_system_preset || false,
        createdAt: partner.created_at,
        updatedAt: partner.updated_at,
      }));

      logger.info(
        `✅ SupabaseMasterService: getAllBusinessPartners完了 - ${businessPartners.length}件取得`
      );
      logger.info('📊 データソース内訳:', {
        rakuhaiProvided: businessPartners.filter(
          p => p.dataSource === BusinessPartnerDataSource.RAKUHAI_PROVIDED
        ).length,
        custom: businessPartners.filter(p => p.dataSource === BusinessPartnerDataSource.CUSTOM)
          .length,
      });

      return businessPartners;
    } catch (error) {
      logger.error('❌ SupabaseMasterService: getAllBusinessPartners失敗:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '全取引先データの取得に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  /**
   * IDを指定して取引先を取得
   * @param id 取引先ID
   * @returns 取引先データまたはnull
   */
  async getBusinessPartnerById(id: string): Promise<BusinessPartner | null> {
    logger.info(`🔍 SupabaseMasterService: getBusinessPartnerById開始 - ID: ${id}`);
    this.ensureSupabaseClient();

    try {
      const { data, error } = await this.supabase!.from('business_partners')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // Not found
          return null;
        }
        throw new DataServiceError(
          `取引先データの取得に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR,
          error
        );
      }

      if (!data) {
        return null;
      }

      const businessPartner: BusinessPartner = {
        id: data.id,
        name: data.name,
        displayName: data.display_name || data.name,
        type: data.type as BusinessPartnerType,
        category: data.category as BusinessPartnerCategory,
        availableRoles: data.available_roles || [],
        defaultAddress: data.default_address,
        defaultPhone: data.default_phone,
        defaultEmail: data.default_email || '',
        defaultContactPerson: data.default_contact_person || '',
        notes: data.notes || '',
        status: data.status,
        companyId: data.company_id,
        dataSource: data.data_source as BusinessPartnerDataSource,
        locations: data.locations || [],
        isSystemPreset: data.is_system_preset || false,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };

      logger.info(`✅ SupabaseMasterService: getBusinessPartnerById完了 - ${businessPartner.name}`);
      return businessPartner;
    } catch (error) {
      logger.error('❌ SupabaseMasterService: getBusinessPartnerById失敗:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '取引先データの取得に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  /**
   * 新しい取引先を作成
   * @param partnerData 取引先データ（IDなし）
   * @returns 作成された取引先データ
   */
  async createBusinessPartner(partnerData: Omit<BusinessPartner, 'id'>): Promise<BusinessPartner> {
    logger.info('🚀 SupabaseMasterService: createBusinessPartner開始:', partnerData.name);
    this.ensureSupabaseClient();

    try {
      const { data, error } = await this.supabase!.from('business_partners')
        .insert({
          name: partnerData.name,
          display_name: partnerData.displayName,
          type: partnerData.type,
          category: partnerData.category,
          available_roles: partnerData.availableRoles,
          default_address: partnerData.defaultAddress,
          default_phone: partnerData.defaultPhone,
          default_email: partnerData.defaultEmail,
          default_contact_person: partnerData.defaultContactPerson,
          notes: partnerData.notes,
          status: partnerData.status || 'Active',
          company_id: partnerData.companyId,
          data_source: partnerData.dataSource,
          locations: partnerData.locations,
          is_system_preset: partnerData.isSystemPreset || false,
        })
        .select('*')
        .single();

      if (error) {
        logger.error('❌ SupabaseMasterService: createBusinessPartner失敗:', error);
        throw new DataServiceError(
          `取引先の作成に失敗しました: ${error.message}`,
          ErrorCodes.DATA_CREATION_FAILED,
          error
        );
      }

      const createdPartner: BusinessPartner = {
        id: data.id,
        name: data.name,
        displayName: data.display_name || data.name,
        type: data.type as BusinessPartnerType,
        category: data.category as BusinessPartnerCategory,
        availableRoles: data.available_roles || [],
        defaultAddress: data.default_address,
        defaultPhone: data.default_phone,
        defaultEmail: data.default_email || '',
        defaultContactPerson: data.default_contact_person || '',
        notes: data.notes || '',
        status: data.status,
        companyId: data.company_id,
        dataSource: data.data_source as BusinessPartnerDataSource,
        locations: data.locations || [],
        isSystemPreset: data.is_system_preset || false,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };

      logger.info(`✅ SupabaseMasterService: createBusinessPartner完了 - ${createdPartner.name}`);
      return createdPartner;
    } catch (error) {
      logger.error('❌ SupabaseMasterService: createBusinessPartner実行エラー:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '取引先の作成に失敗しました',
        ErrorCodes.DATA_CREATION_FAILED,
        error as Error
      );
    }
  }

  // =============================================================================
  // VehicleModel関連メソッド
  // =============================================================================

  /**
   * 指定した会社の車両モデルを全て取得
   * @param companyId 会社ID
   * @returns 車両モデル一覧
   */
  async getAllVehicleModels(companyId: string): Promise<VehicleModel[]> {
    logger.info(`🔍 SupabaseMasterService: getAllVehicleModels開始 - companyId: ${companyId}`);
    this.ensureSupabaseClient();

    try {
      const { data, error } = await this.supabase!.from('vehicle_models')
        .select(
          `
          id,
          name,
          manufacturer_id,
          vehicle_type_id,
          powertrain,
          vehicle_tags,
          data_source,
          created_at,
          updated_at
        `
        )
        .eq('company_id', companyId)
        .order('name');

      if (error) {
        logger.error('VehicleModels取得エラー:', error);
        throw error;
      }

      const vehicleModels = (data || []).map((item: any) => ({
        id: item.id,
        name: item.name,
        manufacturerId: item.manufacturer_id,
        vehicleTypeId: item.vehicle_type_id,
        powertrain: item.powertrain,
        vehicleTags: item.vehicle_tags || [],
        dataSource: item.data_source as VehicleModelDataSource,
      }));

      logger.info(
        `✅ SupabaseMasterService: getAllVehicleModels完了 - ${vehicleModels.length}件取得`
      );
      return vehicleModels;
    } catch (error) {
      logger.error('VehicleModels取得エラー:', error);
      throw new DataServiceError(
        'Vehicle Models取得に失敗しました',
        ErrorCodes.DATA_FETCH_FAILED,
        error as Error
      );
    }
  }

  /**
   * 新しい車両モデルを作成
   * @param model 車両モデルデータ（IDなし）
   * @returns 作成された車両モデルデータ
   */
  async createVehicleModel(model: Omit<VehicleModel, 'id'>): Promise<VehicleModel> {
    logger.info('🚀 SupabaseMasterService: createVehicleModel開始:', model.name);
    this.ensureSupabaseClient();

    try {
      const { data, error } = await this.supabase!.from('vehicle_models')
        .insert({
          name: model.name,
          manufacturer_id: model.manufacturerId,
          vehicle_type_id: model.vehicleTypeId,
          powertrain: model.powertrain || null,
          vehicle_tags: model.vehicleTags || [],
          data_source: model.dataSource,
        })
        .select(
          `
          id,
          name,
          manufacturer_id,
          vehicle_type_id,
          powertrain,
          vehicle_tags,
          data_source,
          created_at,
          updated_at
        `
        )
        .single();

      if (error) {
        logger.error('VehicleModel作成エラー:', error);
        throw error;
      }

      const createdModel: VehicleModel = {
        id: data.id,
        name: data.name,
        manufacturerId: data.manufacturer_id,
        vehicleTypeId: data.vehicle_type_id,
        powertrain: data.powertrain,
        vehicleTags: data.vehicle_tags || [],
        dataSource: data.data_source as VehicleModelDataSource,
      };

      logger.info(`✅ SupabaseMasterService: createVehicleModel完了 - ${createdModel.name}`);
      return createdModel;
    } catch (error) {
      logger.error('VehicleModel作成エラー:', error);
      throw new DataServiceError(
        'Vehicle Model作成に失敗しました',
        ErrorCodes.DATA_CREATION_FAILED,
        error as Error
      );
    }
  }

  // =============================================================================
  // VehicleType関連メソッド
  // =============================================================================

  /**
   * 指定した会社の車種を全て取得
   * @param companyId 会社ID
   * @returns 車種一覧
   */
  async getAllVehicleTypes(companyId: string): Promise<VehicleType[]> {
    logger.info(`🔍 SupabaseMasterService: getAllVehicleTypes開始 - companyId: ${companyId}`);
    this.ensureSupabaseClient();

    try {
      const { data, error } = await this.supabase!.from('vehicle_types')
        .select(
          `
          id,
          name,
          company_id,
          created_at,
          updated_at
        `
        )
        .eq('company_id', companyId)
        .order('name');

      if (error) {
        logger.error('VehicleTypes取得エラー:', error);
        throw error;
      }

      const vehicleTypes = (data || []).map((item: any) => ({
        id: item.id,
        name: item.name,
        companyId: item.company_id,
        createdAt: item.created_at,
        updatedAt: item.updated_at,
      }));

      logger.info(
        `✅ SupabaseMasterService: getAllVehicleTypes完了 - ${vehicleTypes.length}件取得`
      );
      return vehicleTypes;
    } catch (error) {
      logger.error('VehicleTypes取得エラー:', error);
      throw new DataServiceError(
        'Vehicle Types取得に失敗しました',
        ErrorCodes.DATA_FETCH_FAILED,
        error as Error
      );
    }
  }

  // =============================================================================
  // Manufacturer関連メソッド
  // =============================================================================

  /**
   * 指定した会社のメーカーを全て取得
   * @param companyId 会社ID
   * @returns メーカー一覧
   */
  async getAllManufacturers(companyId: string): Promise<Manufacturer[]> {
    logger.info(`🔍 SupabaseMasterService: getAllManufacturers開始 - companyId: ${companyId}`);
    this.ensureSupabaseClient();

    try {
      const { data, error } = await this.supabase!.from('manufacturers')
        .select(
          `
          id,
          name,
          country,
          company_id,
          created_at,
          updated_at
        `
        )
        .eq('company_id', companyId)
        .order('name');

      if (error) {
        logger.error('Manufacturers取得エラー:', error);
        throw error;
      }

      const manufacturers = (data || []).map((item: any) => ({
        id: item.id,
        name: item.name,
        country: item.country || undefined,
        companyId: item.company_id,
        createdAt: item.created_at,
        updatedAt: item.updated_at,
      }));

      logger.info(
        `✅ SupabaseMasterService: getAllManufacturers完了 - ${manufacturers.length}件取得`
      );
      return manufacturers;
    } catch (error) {
      logger.error('Manufacturers取得エラー:', error);
      throw new DataServiceError(
        'Manufacturers取得に失敗しました',
        ErrorCodes.DATA_FETCH_FAILED,
        error as Error
      );
    }
  }
}
